'use client'

import { useTranslations } from 'next-intl'
import { Moon, Sun } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useSyncExternalStore } from 'react'

import { Button } from '@/components/ui/button'
import {
  applyTheme,
  isThemeExemptRoute,
  writeStoredTheme,
  type Theme,
} from '@/lib/theme/theme-init'
import { cn } from '@/lib/utils'

/**
 * The `.dark` class on `<html>` is the single source of truth for the active
 * theme — the init script sets it before paint and `ThemeRouteGuard` changes it
 * on navigation, both outside React. Subscribing to it rather than mirroring it
 * into component state keeps the icon honest no matter which of the three
 * writes last.
 */
function subscribe(onStoreChange: () => void) {
  const observer = new MutationObserver(onStoreChange)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
  return () => observer.disconnect()
}

function getSnapshot(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

// The server cannot know the theme — it lives in `localStorage` and the OS. This
// keeps the hydrating markup matching the server's, and React re-renders with
// the real value immediately afterwards.
function getServerSnapshot(): Theme {
  return 'light'
}

export function ThemeToggle({ className }: { className?: string }) {
  const t = useTranslations()
  const pathname = usePathname()
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  // The reader and the admin console fix their own appearance, so a switch
  // there would be a control that visibly does nothing.
  if (isThemeExemptRoute(pathname)) return null

  const next: Theme = theme === 'dark' ? 'light' : 'dark'
  const switchLabel =
    next === 'dark'
      ? t('common.theme.switchToDark')
      : t('common.theme.switchToLight')

  function toggle() {
    applyTheme(next)
    writeStoredTheme(next)
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={toggle}
      aria-label={switchLabel}
      title={switchLabel}
      className={className}
    >
      {/* Both icons are always mounted, stacked in one cell, and cross-fade.
          Swapping one element for the other is instant by construction — there
          is nothing to transition between — and an instant swap in the middle of
          a page that eases everything else reads as a glitch. */}
      <span className="grid size-4 place-items-center">
        <Sun
          aria-hidden="true"
          className={cn(
            'bk-theme-icon col-start-1 row-start-1',
            theme === 'dark' && 'bk-theme-icon-on',
          )}
        />
        <Moon
          aria-hidden="true"
          className={cn(
            'bk-theme-icon col-start-1 row-start-1',
            theme !== 'dark' && 'bk-theme-icon-on',
          )}
        />
      </span>
    </Button>
  )
}
